import {
  createSaleFeed,
  feedSaleStateFromAuctionStatus,
  isLotStillOpen,
  resolveLiveSource,
  subscribeToLots,
  withLiveFigures,
  type FeedLot,
  type LiveFigures,
  type LiveFiguresRow,
  type LotSubscription,
  type PublicLotRow,
  type SaleFeed,
  type SaleFeedEvents,
  type SaleFeedStatusInfo,
  type YouStanding,
} from '@auctisync/api-client';
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { supabase } from '../lib/supabase';
import { currentAccessToken } from '../lib/accessToken';
import { RENDER_EVERY_MS, type FeedSaleState } from './useSaleFeed';
import { useSession } from './useSession';

/**
 * Live updates for a page whose lots span SEVERAL sales — My bids and the
 * Watchlist. The website's binding over the same shared pieces `useSaleFeed`
 * uses; it holds no rules of its own.
 *
 * One connection per sale that still has open lots on the page:
 *   - a sale the engine runs gets an engine feed with no summary and no view.
 *     The engine sends a signed-in bidder their OWN lots by itself (it knows
 *     whose ceilings it holds) and the WATCHED ones the page names (`watch`).
 *   - a sale on the Supabase book gets Realtime for the sale's lots.
 * Which one is `resolveLiveSource`'s answer, never the page's.
 *
 * A sale whose route cannot be looked up is asked again, backing off from
 * 2 s to 30 s; its lots stay as last read meanwhile.
 */
export interface SaleFeedGroup {
  saleId: string;
  /** Any lot of the sale — the route is per sale. */
  anyLotId: string;
  /** The lots of this sale the viewer watches (engine interest). */
  watch: readonly string[];
  initialSaleState?: FeedSaleState | null | undefined;
}

export interface MultiSaleLive {
  /** The engine's view of a lot, from whichever sale's feed holds it. */
  lot(lotId: string): FeedLot | undefined;
  /** The bidder's own standing on a lot, from the engine. */
  you(lotId: string): YouStanding | undefined;
  /** The latest Realtime row for a lot on a Supabase-book sale. */
  row(lotId: string): PublicLotRow | undefined;
  /** Each engine feed's status, by sale, for the banner (`mostUrgentStatus`). */
  statuses(): SaleFeedStatus[];
  /**
   * Listen across every sale's connection, for what must not wait for a render
   * (the standing and activity panels, 2026-10-11): lots that changed and the
   * bidder's own standing, from the engine; each Realtime row of a sale on the
   * Supabase book. Stable for the life of the page's viewer.
   */
  on: <K extends keyof MultiSaleEvents>(event: K, handler: MultiSaleEvents[K]) => () => void;
  version: number;
}

/** What `MultiSaleLive.on` relays. */
export interface MultiSaleEvents {
  lots: SaleFeedEvents['lots'];
  you: SaleFeedEvents['you'];
  row: (row: PublicLotRow) => void;
}

/** One sale's engine feed status. */
export interface SaleFeedStatus {
  saleId: string;
  info: SaleFeedStatusInfo;
}

interface Entry {
  group: SaleFeedGroup;
  feed: SaleFeed | null;
  sub: LotSubscription | null;
  offs: (() => void)[];
  retry: ReturnType<typeof setTimeout> | null;
  closed: boolean;
}

/** Owns the connections; React only subscribes to its version. */
class SaleFeedSet {
  private readonly entries = new Map<string, Entry>();
  private readonly rows = new Map<string, PublicLotRow>();
  private readonly listeners = new Set<() => void>();
  private readonly relays: { [K in keyof MultiSaleEvents]: Set<MultiSaleEvents[K]> } = {
    lots: new Set(),
    you: new Set(),
    row: new Set(),
  };
  private version = 0;
  private lastNotify = Number.NEGATIVE_INFINITY;
  private trailing: ReturnType<typeof setTimeout> | null = null;

  subscribe = (onChange: () => void): (() => void) => {
    this.listeners.add(onChange);
    return () => {
      this.listeners.delete(onChange);
    };
  };

  getSnapshot = (): number => this.version;

  on = <K extends keyof MultiSaleEvents>(event: K, handler: MultiSaleEvents[K]): (() => void) => {
    const set = this.relays[event] as Set<MultiSaleEvents[K]>;
    set.add(handler);
    return () => {
      set.delete(handler);
    };
  };

  private emit<K extends keyof MultiSaleEvents>(
    event: K,
    ...args: Parameters<MultiSaleEvents[K]>
  ): void {
    for (const handler of [...(this.relays[event] as Set<MultiSaleEvents[K]>)]) {
      (handler as (...a: Parameters<MultiSaleEvents[K]>) => void)(...args);
    }
  }

  /** Make the open connections match `groups`. */
  sync(groups: readonly SaleFeedGroup[]): void {
    const wanted = new Map(groups.map((g) => [g.saleId, g]));
    for (const [saleId, entry] of this.entries) {
      const next = wanted.get(saleId);
      if (next === undefined || next.anyLotId !== entry.group.anyLotId) this.drop(saleId);
    }
    for (const group of groups) {
      const entry = this.entries.get(group.saleId);
      if (entry === undefined) {
        this.open(group);
        continue;
      }
      entry.group = group;
      entry.feed?.setWatch(group.watch);
    }
  }

  lot(lotId: string): FeedLot | undefined {
    for (const entry of this.entries.values()) {
      const lot = entry.feed?.lot(lotId);
      if (lot !== undefined) return lot;
    }
    return undefined;
  }

  you(lotId: string): YouStanding | undefined {
    for (const entry of this.entries.values()) {
      const you = entry.feed?.you(lotId);
      if (you !== undefined) return you;
    }
    return undefined;
  }

  row(lotId: string): PublicLotRow | undefined {
    return this.rows.get(lotId);
  }

  statuses(): SaleFeedStatus[] {
    const out: SaleFeedStatus[] = [];
    for (const [saleId, entry] of this.entries) {
      if (entry.feed !== null) out.push({ saleId, info: entry.feed.status() });
    }
    return out;
  }

  dispose(): void {
    for (const saleId of [...this.entries.keys()]) this.drop(saleId);
    if (this.trailing !== null) clearTimeout(this.trailing);
    this.trailing = null;
    this.listeners.clear();
    for (const set of Object.values(this.relays)) set.clear();
  }

  private open(group: SaleFeedGroup, attempt = 0): void {
    const entry: Entry = {
      group,
      feed: null,
      sub: null,
      offs: [],
      retry: null,
      closed: false,
    };
    this.entries.set(group.saleId, entry);
    void this.connect(entry, attempt);
  }

  private async connect(entry: Entry, attempt: number): Promise<void> {
    const answer = await resolveLiveSource(supabase, entry.group.anyLotId, {
      fresh: attempt > 0,
    });
    if (entry.closed) return;
    if (!answer.ok) {
      const delay = Math.min(30_000, 2_000 * 2 ** attempt);
      entry.retry = setTimeout(() => {
        entry.retry = null;
        if (!entry.closed) void this.connect(entry, attempt + 1);
      }, delay);
      return;
    }
    const source = answer.source;
    if (source.kind === 'engine') {
      const feed = createSaleFeed({
        engineUrl: source.engineUrl,
        engineAuctionId: source.engineAuctionId,
        houseId: source.houseId,
        getToken: currentAccessToken,
        watch: entry.group.watch,
        initialSaleState: entry.group.initialSaleState ?? null,
      });
      entry.feed = feed;
      const bump = () => this.bump();
      entry.offs = [
        feed.on('lots', bump),
        feed.on('you', bump),
        feed.on('status', bump),
        feed.on('sale', bump),
        feed.on('lots', (changes) => this.emit('lots', changes)),
        feed.on('you', (you, prev) => this.emit('you', you, prev)),
      ];
    } else {
      entry.sub = subscribeToLots(supabase, {
        target: { kind: 'auction', auctionId: entry.group.saleId },
        onUpdate: (row) => {
          this.rows.set(row.id, row);
          this.bump();
          this.emit('row', row);
        },
      });
    }
    this.bump();
  }

  private drop(saleId: string): void {
    const entry = this.entries.get(saleId);
    if (entry === undefined) return;
    entry.closed = true;
    if (entry.retry !== null) clearTimeout(entry.retry);
    for (const off of entry.offs) off();
    entry.feed?.close();
    entry.sub?.close();
    this.entries.delete(saleId);
    this.bump();
  }

  private bump(): void {
    this.version += 1;
    if (this.trailing !== null) return;
    const wait = this.lastNotify + RENDER_EVERY_MS - Date.now();
    const notify = () => {
      this.lastNotify = Date.now();
      for (const listener of [...this.listeners]) listener();
    };
    if (wait <= 0) {
      notify();
      return;
    }
    this.trailing = setTimeout(() => {
      this.trailing = null;
      notify();
    }, wait);
  }
}

/**
 * One group per sale that still has an open lot on the page — a closed lot
 * changes no more, and a sale with nothing open needs no connection. `watch`
 * is the open lots of that sale in `watched`.
 */
export function saleGroupsFor(
  lots: readonly { id: string; auction_id: string; status: string }[],
  watched: ReadonlySet<string>,
  saleStatus: ReadonlyMap<string, string | null>,
): SaleFeedGroup[] {
  const bySale = new Map<string, string[]>();
  for (const lot of lots) {
    if (!isLotStillOpen(lot)) continue;
    const ids = bySale.get(lot.auction_id) ?? [];
    if (!ids.includes(lot.id)) ids.push(lot.id);
    bySale.set(lot.auction_id, ids);
  }
  return [...bySale].map(([saleId, ids]) => ({
    saleId,
    anyLotId: ids[0] as string,
    watch: ids.filter((id) => watched.has(id)),
    initialSaleState: feedSaleStateFromAuctionStatus(saleStatus.get(saleId)),
  }));
}

/**
 * A lot row with whatever is live for it laid over: the Supabase book's
 * latest Realtime row, then the engine's figures (`withLiveFigures`, the
 * shared rule — the engine wins every figure it carries).
 */
export function liveRowFrom<R extends LiveFiguresRow & { id: string }>(
  row: R,
  live: Pick<MultiSaleLive, 'lot' | 'row'>,
): Omit<R, keyof LiveFigures> & LiveFigures {
  const realtime = live.row(row.id);
  const base =
    realtime === undefined
      ? row
      : {
          ...row,
          current_bid_cents: realtime.current_bid_cents,
          bid_count: realtime.bid_count,
          ends_at: realtime.ends_at,
          status: realtime.status,
        };
  return withLiveFigures(base, live.lot(row.id));
}

const groupsKey = (groups: readonly SaleFeedGroup[]): string =>
  groups
    .map((g) => `${g.saleId}:${g.anyLotId}:${g.watch.join(',')}:${g.initialSaleState ?? ''}`)
    .join('|');

export function useSaleFeeds(groups: readonly SaleFeedGroup[]): MultiSaleLive {
  const { session, loading } = useSession();
  const userId = session?.user.id ?? null;
  const [set, setSet] = useState<SaleFeedSet | null>(null);

  // A new viewer is a new set of sockets: the engine binds the role on connect.
  // Not before the session is known (see `useSaleFeed`).
  useEffect(() => {
    if (loading) {
      setSet(null);
      return;
    }
    const created = new SaleFeedSet();
    setSet(created);
    return () => created.dispose();
  }, [userId, loading]);

  const latest = useRef(groups);
  latest.current = groups;
  const key = groupsKey(groups);
  useEffect(() => {
    set?.sync(latest.current);
  }, [set, key]);

  const noop = useMemo(() => ({ subscribe: () => () => {}, getSnapshot: () => 0 }), []);
  const on = useCallback<MultiSaleLive['on']>(
    (event, handler) => set?.on(event, handler) ?? (() => {}),
    [set],
  );
  const store = set ?? noop;
  const version = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);

  return useMemo<MultiSaleLive>(
    () => ({
      lot: (id) => set?.lot(id),
      you: (id) => set?.you(id),
      row: (id) => set?.row(id),
      statuses: () => set?.statuses() ?? [],
      on,
      version,
    }),
    [set, on, version],
  );
}
